import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
    FaEye, 
    FaSearch, 
    FaStore, 
    FaBan, 
    FaCheck, 
    FaToggleOn, 
    FaToggleOff,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaFilter,
    FaPrint,
    FaEyeSlash
} from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import axios from 'axios';
import { fetchStores, toggleStoreBan, toggleStoreStatus, clearError, clearSuccess } from '../../../redux/Slices/AdminStoresSlice';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import PrintInvoicesModal from '../../../shared/components/PrintInvoicesModal';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const columnHelper = createColumnHelper();

const AdminStores = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { stores, pagination, loading, updating, error, success } = useSelector((state) => state.adminStores);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [searchName, setSearchName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [debouncedSearchName, setDebouncedSearchName] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', data: null });
    const [printModal, setPrintModal] = useState({ isOpen: false, storeId: null });
    
    // Table states
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    // دالة لتصحيح عرض رقم الهاتف
    const formatPhoneNumber = (phone) => {
        if (!phone) return 'غير محدد';
        
        // تحويل إلى نص للتأكد
        let phoneStr = String(phone).trim();
        
        // إزالة أي مسافات أو رموز غير مرغوب فيها
        phoneStr = phoneStr.replace(/\s+/g, '');
        
        // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
        if (phoneStr.startsWith('+')) {
            return phoneStr.slice(1) + '+';
        }
        
        // إذا كان الرقم ينتهي بـ + (مثل 201025376604+) - نتركه كما هو
        if (phoneStr.endsWith('+')) {
            return phoneStr;
        }
        
        // إذا كان الرقم بدون + (مثل 201025376604) - نضيف + في النهاية
        return phoneStr + '+';
    };

    useEffect(() => {
        setTitle('إدارة المتاجر');
        // جلب التصنيفات أولاً
        fetchCategories();
    }, [setTitle]);

    // Debounce search name
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchName(searchName.trim());
        }, 500);

        return () => clearTimeout(timer);
    }, [searchName]);

    useEffect(() => {
        if (searchName.trim() !== debouncedSearchName) {
            setIsSearching(true);
        }
        
        // انتظار جلب التصنيفات أولاً
        if (categories.length > 0 || selectedCategory === '') {
        dispatch(fetchStores({ 
            page: currentPage, 
            name: debouncedSearchName, 
            category_id: selectedCategory 
        })).finally(() => {
            setIsSearching(false);
        });
        }
    }, [dispatch, currentPage, debouncedSearchName, selectedCategory, categories]);


    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            dispatch(fetchStores({ 
                page: currentPage, 
                name: searchName, 
                category_id: selectedCategory 
            }));
        }
    }, [success, dispatch, currentPage, searchName, selectedCategory]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                // البحث عن التوكن في أماكن مختلفة
                if (parsed.data?.token?.access_token) {
                    return String(parsed.data.token.access_token);
                }
                if (parsed.data?.access_token) {
                    return String(parsed.data.access_token);
                }
                if (parsed.data?.token) {
                    return String(parsed.data.token);
                }
                if (parsed.token) {
                    return String(parsed.token);
                }
                return null;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchCategories = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data?.status === 200 && response.data?.data) {
                setCategories(response.data.data);
            }
        } catch (error) {
        }
    };

    const handleViewDetails = (storeId) => {
        navigate(`/admin-stores/${storeId}`);
    };

    const handleViewBranches = (storeId) => {
        navigate(`/admin-stores/${storeId}/branches`);
    };

    const handleToggleBan = async (storeId, currentBan) => {
        const newBanStatus = currentBan === "1" || currentBan === 1 ? 0 : 1;
        const confirmMessage = newBanStatus === 1 ? 'هل أنت متأكد من حظر هذا المتجر؟' : 'هل أنت متأكد من إلغاء حظر هذا المتجر؟';
        
        setConfirmModal({
            isOpen: true,
            type: 'ban',
            data: { storeId, status: newBanStatus },
            message: confirmMessage,
            title: newBanStatus === 1 ? 'تأكيد الحظر' : 'تأكيد إلغاء الحظر',
            confirmButtonColor: newBanStatus === 1 ? '#dc3545' : '#28a745'
        });
    };

    const handleToggleStatus = async (storeId, currentStatus) => {
        const newStatus = currentStatus === "1" || currentStatus === 1 ? 0 : 1;
        const confirmMessage = newStatus === 1 ? 'هل أنت متأكد من تفعيل هذا المتجر؟' : 'هل أنت متأكد من إلغاء تفعيل هذا المتجر؟';
        
        setConfirmModal({
            isOpen: true,
            type: 'status',
            data: { storeId, status: newStatus },
            message: confirmMessage,
            title: newStatus === 1 ? 'تأكيد التفعيل' : 'تأكيد إلغاء التفعيل',
            confirmButtonColor: newStatus === 1 ? '#28a745' : '#ffc107'
        });
    };

    const handleConfirmAction = () => {
        if (confirmModal.type === 'ban') {
            dispatch(toggleStoreBan(confirmModal.data));
        } else if (confirmModal.type === 'status') {
            dispatch(toggleStoreStatus(confirmModal.data));
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setDebouncedSearchName(searchName.trim());
    };

    const handleClearFilters = () => {
        setSearchName('');
        setSelectedCategory('');
        setCurrentPage(1);
        setDebouncedSearchName('');
        setIsSearching(false);
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger text-white">محظور</span>;
        }
        return <span className="badge bg-success text-white">غير محظور</span>;
    };

    // دالة للحصول على اسم التصنيف
    const getCategoryName = (categoryId) => {
        if (!categoryId) return 'غير محدد';
        if (!categories.length) return 'جاري التحميل...';
        
        // تحويل categoryId إلى number للمقارنة
        const numericCategoryId = parseInt(categoryId);
        const category = categories.find(cat => cat.id === numericCategoryId);
        
        
        return category?.name?.ar || category?.name || 'غير محدد';
    };

    // تعريف الأعمدة
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                                                        <div className="order-number-badge">
                        #{(currentPage - 1) * 10 + row.index + 1}
                                                        </div>
                ),
                size: 80,
                minSize: 60,
                maxSize: 100,
            }),
            columnHelper.accessor('logo', {
                id: 'logo',
                header: 'اللوجو',
                cell: ({ row }) => {
                    const store = row.original;
        return (
                        <div className="d-flex justify-content-center align-items-center">
                                                        <div className="store-logo-container">
                                                            {store.logo && store.logo !== 'logo' ? (
                                                                <img
                                                                    src={store?.logo}
                                                                    alt={store.market_name}
                                                                    className="store-logo"
                                                                />
                                                            ) : (
                                                                <div className="no-logo-placeholder">
                                                                    <FaStore />
                                                                </div>
                                                            )}
                            </div>
                                                        </div>
                    );
                },
                size: 120,
                minSize: 100,
                maxSize: 150,
            }),
            columnHelper.accessor('market_name', {
                id: 'name',
                header: 'اسم المتجر',
                cell: ({ row }) => (
                                                        <div className="store-name">
                        {row.original.market_name || 'غير محدد'}
                                                        </div>
                ),
                size: 180,
                minSize: 120,
                maxSize: 300,
            }),
            columnHelper.accessor('phone', {
                id: 'phone',
                header: 'رقم الهاتف',
                cell: ({ row }) => (
                                                        <div className="phone-badge">
                        {formatPhoneNumber(row.original.phone)}
                                                        </div>
                ),
                size: 160,
                minSize: 130,
                maxSize: 200,
            }),
            columnHelper.accessor('email', {
                id: 'email',
                header: 'البريد الإلكتروني',
                cell: ({ row }) => (
                    <div className="email-display">
                        {row.original.email || 'غير محدد'}
                                                        </div>
                ),
                size: 400,
                minSize: 350,
                maxSize: 500,
            }),
            columnHelper.accessor('category_id', {
                id: 'category',
                header: 'التصنيف',
                cell: ({ row }) => (
                                                        <div className="category-name">
                        {getCategoryName(row.original.category_id)}
                                                        </div>
                ),
                size: 140,
                minSize: 100,
                maxSize: 200,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'حالة النشاط',
                cell: ({ row }) => getStatusBadge(row.original.status),
                size: 130,
                minSize: 100,
                maxSize: 160,
            }),
            columnHelper.accessor('ban', {
                id: 'ban',
                header: 'حالة الحظر',
                cell: ({ row }) => getBanBadge(row.original.ban),
                size: 130,
                minSize: 100,
                maxSize: 160,
            }),
            columnHelper.accessor('created_at', {
                id: 'date',
                header: 'تاريخ التسجيل',
                cell: ({ row }) => (
                                                        <div className="date-display">
                        {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                                        </div>
                ),
                size: 140,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.display({
                id: 'branches',
                header: 'الفروع',
                cell: ({ row }) => (
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-branches"
                            onClick={() => handleViewBranches(row.original.id)}
                                                                title="عرض الفروع">
                                                                <FaStore />
                                                            </motion.button>
                                                        </div>
                ),
                size: 80,
                minSize: 70,
                maxSize: 100,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const store = row.original;
                    return (
                                                        <div className="action-buttons">
                                                            <motion.button
                                whileHover={{ scale: 1.05, y: -1 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-view"
                                                                onClick={() => handleViewDetails(store.id)}
                                                                title="عرض التفاصيل">
                                                                <FaEye />
                                                            </motion.button>
                                                            {store.status === "1" || store.status === 1 ? (
                                                                <motion.button
                                    whileHover={{ scale: 1.05, y: -1 }}
                                                                    whileTap={{ scale: 0.95 }}
                                    className="btn btn-toggle"
                                                                    onClick={() => handleToggleStatus(store.id, store.status)}
                                                                    disabled={updating}
                                                                    title="إلغاء التفعيل">
                                    <FaToggleOff />
                                                                </motion.button>
                                                            ) : (
                                                                <motion.button
                                    whileHover={{ scale: 1.05, y: -1 }}
                                                                    whileTap={{ scale: 0.95 }}
                                    className="btn btn-toggle"
                                                                    onClick={() => handleToggleStatus(store.id, store.status)}
                                                                    disabled={updating}
                                                                    title="تفعيل المتجر">
                                    <FaToggleOn />
                                                                </motion.button>
                                                            )}
                                                            {store.ban === "1" || store.ban === 1 ? (
                                                                <motion.button
                                    whileHover={{ scale: 1.05, y: -1 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-unban"
                                                                    onClick={() => handleToggleBan(store.id, store.ban)}
                                                                    disabled={updating}
                                                                    title="إلغاء الحظر">
                                                                    <FaCheck />
                                                                </motion.button>
                                                            ) : (
                                                                <motion.button
                                    whileHover={{ scale: 1.05, y: -1 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-ban"
                                                                    onClick={() => handleToggleBan(store.id, store.ban)}
                                                                    disabled={updating}
                                                                    title="حظر المتجر">
                                                                    <FaBan />
                                                                </motion.button>
                                                            )}
                                                            <motion.button
                                                                whileHover={{ scale: 1.05, y: -1 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-print"
                                                                onClick={() => setPrintModal({ isOpen: true, storeId: store.id })}
                                                                title="طباعة فواتير"
                                                                style={{ fontSize: '1.1rem' }}>
                                                                <FaPrint />
                                                            </motion.button>
                                                        </div>
                    );
                },
                size: 190,
                minSize: 170,
                maxSize: 210,
            }),
        ],
        [currentPage, updating, categories]
    );

    // إعداد الجدول
    const table = useReactTable({
        data: stores || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });


    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة المتاجر</h4>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في المتاجر..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {stores?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">التصنيف</label>
                                        <select
                                            className="form-select"
                                            value={selectedCategory}
                                            onChange={(e) => {
                                                setSelectedCategory(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="">جميع التصنيفات</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name?.ar || category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">حالة النشاط</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'status')?.value ?? ''}
                                            onChange={(e) => {
                                                const statusFilter = e.target.value;
                                                setColumnFilters(prev => 
                                                    prev.filter(f => f.id !== 'status').concat(
                                                        statusFilter ? [{ id: 'status', value: statusFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="1">نشط</option>
                                            <option value="0">غير نشط</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading || isSearching ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">
                                    {isSearching ? 'جاري البحث...' : 'جاري تحميل المتاجر...'}
                                </p>
                            </div>
                        ) : stores && Array.isArray(stores) && stores.length > 0 ? (
                            <div className="table-responsive" style={{ 
                                overflowX: 'auto', 
                                overflowY: 'visible',
                                maxHeight: 'none',
                                width: '100%'
                            }}>
                                <table className="table mb-0 table-modern" style={{ 
                                    backgroundColor: 'transparent', 
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                    width: '100%',
                                    minWidth: '1400px'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{ 
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                                </tr>
                                            ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                        style={{ 
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize(),
                                                            verticalAlign: 'middle'
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaStore size={48} className="mb-3" />
                                    <p>لا توجد متاجر حالياً</p>
                                </div>
                            </div>
                        )}
                                </div>

                    
                    {/* Table Pagination */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">عرض</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                                        >
                                            {[5, 10, 20, 30, 50].map(pageSize => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted">
                                            من {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} إلى{' '}
                                            {Math.min(
                                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                                table.getFilteredRowModel().rows.length
                                            )}{' '}
                                            من أصل {table.getFilteredRowModel().rows.length} نتيجة
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                        </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* API Pagination */}
                {pagination && (pagination.total > 0 || stores?.length > 0) && (
                    <div className="card-footer bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.total_pages || pagination?.last_page || 1;
                                                    if (value <= totalPages) {
                                                        setCurrentPage(value);
                                                    }
                                                }
                                            }}
                                            min="1"
                                            max={pagination?.total_pages || pagination?.last_page || 1}
                                            style={{ width: '60px' }}
                                        />
                    </div>
                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                    </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || currentPage} من {pagination.total_pages || pagination.last_page || 1}
                                    </span>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        التالية
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setCurrentPage(pagination.total_pages || pagination.last_page || 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <PrintInvoicesModal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal({ isOpen: false, storeId: null })}
                storeId={printModal.storeId}
                baseUrl={BASE_URL}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: '', data: null })}
                onConfirm={handleConfirmAction}
                title={confirmModal.title || 'تأكيد العملية'}
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="تأكيد"
                cancelText="إلغاء"
                confirmButtonColor={confirmModal.confirmButtonColor || '#dc3545'}
                type={confirmModal.type === 'ban' && confirmModal.data?.status === 1 ? 'danger' : 'warning'}
            />
        </div>
    );
};

export default AdminStores;

// Attach print modal at the bottom


// CSS للجدول المحسن
const styles = `
.btn-toggle:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Store Logo Styling */
.store-logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 8px;
    overflow: hidden;
    background-color: #f8f9fa;
    border: 2px solid #e9ecef;
    transition: all 0.3s ease;
    margin: 0 auto;
}

.store-logo-container:hover {
    border-color: #184194;
    box-shadow: 0 2px 8px rgba(24, 65, 148, 0.2);
    transform: scale(1.05);
}

.store-logo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 6px;
}

.no-logo-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
    color: #6c757d;
    font-size: 24px;
    border-radius: 6px;
}

  .table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1200px;
  }
  
  .table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .table-modern tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f1f3f4;
  }
  
  .table-modern tbody tr:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    text-align: center;
}

/* Ensure logo column is perfectly centered */
.table-modern td:has(.store-logo-container) {
    text-align: center;
    vertical-align: middle;
    display: table-cell;
}

/* تصميم أزرار الإجراءات */
.action-buttons {
    display: flex !important;
    gap: 0.3rem !important;
    justify-content: center !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    max-width: 180px !important;
    margin: 0 auto !important;
    padding: 0 !important;
    align-items: center !important;
}

.btn-view, .btn-ban, .btn-unban, .btn-toggle, .btn-print {
    border: none !important;
    color: white !important;
    padding: 0 !important;
    border-radius: 6px !important;
    font-size: 0 !important;
    font-weight: 600 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
    width: 32px !important;
    height: 32px !important;
    white-space: nowrap !important;
    text-align: center !important;
    min-width: 32px !important;
    max-width: 32px !important;
    overflow: hidden !important;
}

.btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3) !important;
}

.btn-print {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3) !important;
}

.btn-ban {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3) !important;
}

.btn-unban {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3) !important;
}

.btn-toggle {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3) !important;
}

.btn-view:hover, .btn-ban:hover, .btn-unban:hover, .btn-toggle:hover, .btn-print:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-view *, .btn-ban *, .btn-unban *, .btn-toggle *, .btn-print * {
    color: white !important;
    font-size: 1rem;
}

.btn-view, .btn-ban, .btn-unban, .btn-toggle {
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-view svg, .btn-ban svg, .btn-unban svg, .btn-toggle svg, .btn-print svg {
    width: 16px !important;
    height: 16px !important;
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
}

.btn-branches {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
    box-shadow: 0 2px 4px rgba(139, 92, 246, 0.3) !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 6px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    color: white !important;
    font-size: 0 !important;
    transition: all 0.3s ease !important;
    padding: 0 !important;
    min-width: 32px !important;
    max-width: 32px !important;
    overflow: hidden !important;
}

.btn-branches svg {
    width: 14px !important;
    height: 14px !important;
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
}

.btn-branches:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

/* تصميم الإيميل */
.email-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    white-space: nowrap;
    overflow: visible;
    text-overflow: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.4;
    max-width: 100%;
    min-width: 200px;
}

/* تصميم اسم المتجر */
.store-name {
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

/* تصميم رقم الهاتف */
.phone-badge {
    background: #f0f9ff;
    color: #0369a1;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #bae6fd;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
}

/* تصميم التصنيف */
.category-name {
    background: #f0fdf4;
    color: #166534;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #bbf7d0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.4;
}

/* تصميم التاريخ */
.date-display {
    background: #fef3c7;
    color: #92400e;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #fde68a;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.4;
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-height: none;
    position: relative;
}

/* Force scrollbar to always be visible */
.table-responsive::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 12px;
}

.table-responsive::-webkit-scrollbar:horizontal {
    height: 12px;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 6px;
    border: 2px solid #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 12px;
    width: 12px;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
    margin: 0 10px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 6px;
    transition: all 0.3s ease;
    border: 2px solid #f1f1f1;
  }
  
.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.05);
}

.table-responsive::-webkit-scrollbar-corner {
    background: #f1f1f1;
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: auto;
    scrollbar-color: #184194 #f1f1f1;
}

/* Ensure scrollbar is always visible */
.table-responsive {
    overflow-x: scroll !important;
    overflow-y: visible !important;
}

/* Add padding to prevent content from being hidden behind scrollbar */
.table-responsive::after {
    content: '';
    display: block;
    height: 1px;
    width: 100%;
}

/* Force horizontal scrollbar to be visible */
.table-responsive {
    overflow-x: scroll !important;
    overflow-y: visible !important;
    -webkit-overflow-scrolling: touch;
}

/* Ensure table has minimum width to trigger scrollbar */
.table-modern {
    min-width: 1400px !important;
    width: max-content !important;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 16px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 12px 16px;
    font-size: 16px;
}

.search-input {
    border: none;
    padding: 12px 16px;
    font-size: 14px;
    background: white;
    transition: all 0.3s ease;
}

.search-input:focus {
    border: none;
    box-shadow: none;
    background: #f8f9fa;
}

.search-input::placeholder {
    color: #6c757d;
    font-style: italic;
}

.input-group .form-control:focus {
    border-color: #184194;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25);
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

/* Original Pagination Styling */
.pagination {
    margin-bottom: 0;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
}

.pagination .page-item {
    margin: 0.125rem;
}

.pagination .page-link {
    color: #184194;
    border-color: #dee2e6;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.pagination .page-link:hover {
    color: #fff;
    background-color: #184194;
    border-color: #184194;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.pagination .page-item.active .page-link {
    background-color: #184194;
    border-color: #184194;
    color: white;
}

  .pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
    cursor: not-allowed;
  }
  
@media (max-width: 768px) {
    .action-buttons {
        flex-direction: row;
        gap: 0.25rem;
        max-width: 160px;
        flex-wrap: wrap;
    }
    
    .action-buttons .btn {
        width: 32px;
        height: 32px;
        font-size: 0.8rem;
        padding: 0.25rem;
    }
    
    .table-modern {
        min-width: 800px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .pagination {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
    }
    
    .pagination .page-item {
        margin: 0.125rem;
    }
    
    .pagination .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
}

/* إجبار الأزرار على أن تكون أيقونات فقط */
.action-buttons .btn {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    font-size: 0 !important;
    line-height: 0 !important;
    text-indent: -9999px !important;
    overflow: hidden !important;
    border-radius: 6px !important;
}

/* ألوان الأزرار */
.action-buttons .btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.action-buttons .btn-ban {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
}

.action-buttons .btn-unban {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
}

.action-buttons .btn-toggle {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    max-width: 32px !important;
}

.action-buttons .btn-branches {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
}

.action-buttons .btn svg {
    display: block !important;
    width: 14px !important;
    height: 14px !important;
    margin: 0 !important;
    padding: 0 !important;
    text-indent: 0 !important;
}

/* إخفاء أي نصوص في الأزرار */
.action-buttons .btn span {
    display: none !important;
}

/* إجبار زر التفعيل على أن يكون مربع صغير */
.action-buttons .btn-toggle {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    max-width: 32px !important;
    padding: 0 !important;
    margin: 0 !important;
    font-size: 0 !important;
    line-height: 0 !important;
    text-indent: -9999px !important;
    overflow: hidden !important;
    white-space: nowrap !important;
}

/* تحسين عرض الإيميل */
.email-display {
    background: #f8fafc !important;
    color: #64748b !important;
    padding: 0.5rem 1rem !important;
    border-radius: 8px !important;
    font-size: 0.875rem !important;
    border: 1px solid #e2e8f0 !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: unset !important;
    display: inline-block !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    line-height: 1.4 !important;
    max-width: none !important;
    min-width: 300px !important;
    width: auto !important;
    max-width: 500px !important;
}

/* إجبار خلية الإيميل على التمدد */
.table-modern td:has(.email-display) {
    width: auto !important;
    min-width: 400px !important;
    max-width: 500px !important;
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
